import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, ChevronRight, Loader2, Snowflake } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ColumnCells, ResizableTableHead, RowActions } from '@/components/table'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { cn } from '@/lib/utils'
import { useStorageTransfers } from '@/hooks/useStorageTransfers'
import { TransferStatusChip } from '@/components/equipment/storage/replace/TransferStatusChip'
import type { StorageTransfer, StorageTransferStatus } from '@/types/storage'
import { PageHeading } from '@/components/layout/PageHeading'

/**
 * Every transfer plan in the tenant.
 *
 * This page is why an approved plan can be found again at all. Without it the
 * only route to one was the redirect that created it, so closing the tab lost
 * a READY plan that was freezing a freezer — the record existed, the endpoint
 * existed, and nothing called it.
 *
 * Each row states the two units, because "which freezer is going where" is the
 * question somebody arrives with.
 */

// Per-user column layout, the same contract every other table here follows.
// The id is released — changing it discards saved layouts.
const TRANSFERS_TABLE_ID = 'storage-transfers'

const TRANSFERS_COLUMNS: TableColumnDef[] = [
  { id: 'source', header: 'From', locked: true, defaultWidth: 180, sortable: true },
  { id: 'target', header: 'To', defaultWidth: 180, sortable: true },
  { id: 'reason', header: 'Reason', defaultWidth: 220, sortable: true },
  { id: 'disposition', header: 'Source afterwards', defaultWidth: 170, sortable: true },
  { id: 'moves', header: 'Moves', defaultWidth: 100, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 130, sortable: true },
  { id: 'planned', header: 'Planned', defaultWidth: 130, sortable: true },
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 90 },
]

const HEAD_CLASSES: Record<string, string | undefined> = {
  moves: 'text-right',
  actions: 'w-20',
}

const STATUSES: Array<{ value: StorageTransferStatus | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'All plans' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'READY', label: 'Approved' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
]

/** The disposition in three words, for a column rather than a sentence. */
const DISPOSITION_WORD: Record<string, string> = {
  RETURN_TO_SERVICE: 'Comes back',
  DECOMMISSION: 'Retired',
  REMAINS_IN_SERVICE: 'Stays in service',
}

// Header sorting (z8nrz7e4h6). Unsorted, plans keep the API's order.
const STATUS_LABEL = new Map(STATUSES.map((s) => [s.value, s.label]))

const TRANSFERS_SORT: SortAccessors<StorageTransfer> = {
  source: (t) => t.sourceRoot?.code,
  target: (t) => t.targetRoot?.code,
  reason: (t) => t.reason?.label ?? t.reasonCode,
  disposition: (t) => DISPOSITION_WORD[t.disposition] ?? t.disposition,
  moves: (t) => t._count?.moves ?? 0,
  status: (t) => STATUS_LABEL.get(t.status) ?? t.status,
  planned: (t) => t.createdAt,
}

export function StorageTransfersPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<StorageTransferStatus | 'ALL'>('ALL')
  const transfers = useStorageTransfers(status === 'ALL' ? {} : { status })
  const cols = useTableColumns(TRANSFERS_TABLE_ID, TRANSFERS_COLUMNS)

  const rows = useSortedRows(transfers.data, cols.sort, TRANSFERS_SORT)

  return (
    <div className='space-y-3 p-4 md:p-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <Snowflake className='h-5 w-5 shrink-0 text-sky-600' />
          <PageHeading
            title='Transfer plans'
            description="Where a unit's contents are planned to go. Nothing moves until a technician works the plan."
          />
        </div>
        <div className='flex items-end gap-2'>
          <div className='space-y-1'>
            <Label className='text-xs'>Status</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as StorageTransferStatus | 'ALL')}
            >
              <SelectTrigger className='h-9 w-44'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant='outline'
            size='sm'
            className='h-9'
            onClick={() => navigate('/equipment?tab=storage')}
          >
            Storage units
          </Button>
        </div>
      </div>

      {transfers.isLoading ? (
        <p className='py-10 text-center text-sm text-muted-foreground'>
          <Loader2 className='mr-2 inline h-4 w-4 animate-spin' />
          Loading plans…
        </p>
      ) : transfers.error ? (
        <p className='text-sm text-destructive'>Could not load plans: {transfers.error.message}</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className='space-y-1 py-10 text-center text-sm text-muted-foreground'>
            {/* Two different absences: nothing at all, versus nothing matching
                the filter. One message for both sends people looking in the
                wrong place. */}
            <p className='font-medium text-foreground'>
              {status === 'ALL' ? 'No transfer plans yet.' : 'No plans with that status.'}
            </p>
            <p>
              {status === 'ALL'
                ? 'Plan one from a storage unit — Replace unit on the Storage tab.'
                : 'Try All plans.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className='overflow-hidden rounded-lg border'>
          <Table>
            <TableHeader>
              <TableRow className='hover:bg-transparent'>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow
                  key={t.id}
                  className='cursor-pointer'
                  onClick={() => navigate(`/inventory/storage-transfers/${t.id}`)}
                >
                  <ColumnCells
                    columns={cols}
                    cells={{
                      source: (
                        <TableCell>
                          <div className='font-medium'>{t.sourceRoot?.code ?? '—'}</div>
                          {t.sourceRoot?.name && (
                            <div className='text-xs text-muted-foreground'>
                              {t.sourceRoot.name}
                            </div>
                          )}
                        </TableCell>
                      ),
                      target: (
                        <TableCell>
                          <div className='flex items-center gap-1.5 font-medium'>
                            <ArrowRight className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
                            {t.targetRoot?.code ?? '—'}
                          </div>
                          {t.targetRoot?.name && (
                            <div className='pl-5 text-xs text-muted-foreground'>
                              {t.targetRoot.name}
                            </div>
                          )}
                        </TableCell>
                      ),
                      reason: (
                        <TableCell className='text-sm text-muted-foreground'>
                          {t.reason?.label ?? t.reasonCode}
                        </TableCell>
                      ),
                      disposition: (
                        <TableCell className='text-sm text-muted-foreground'>
                          {DISPOSITION_WORD[t.disposition] ?? t.disposition}
                        </TableCell>
                      ),
                      moves: (
                        <TableCell className='text-right tabular-nums'>
                          {t._count?.moves ?? 0}
                        </TableCell>
                      ),
                      status: (
                        <TableCell>
                          <TransferStatusChip status={t.status} />
                        </TableCell>
                      ),
                      planned: (
                        <TableCell className='whitespace-nowrap text-sm text-muted-foreground'>
                          {new Date(t.createdAt).toLocaleDateString()}
                        </TableCell>
                      ),
                      actions: (
                        <TableCell>
                          <RowActions>
                            {/* The row is clickable, but a visible affordance
                                stops the table reading as a static report. */}
                            <Button
                              size='icon'
                              variant='ghost'
                              className={cn('h-7 w-7 text-muted-foreground')}
                              aria-label={`Open the plan for ${t.sourceRoot?.code ?? 'this unit'}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/inventory/storage-transfers/${t.id}`)
                              }}
                            >
                              <ChevronRight className='h-4 w-4' />
                            </Button>
                          </RowActions>
                        </TableCell>
                      ),
                    }}
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
